import { SiteUrls } from '@tableau/shared-urls';

import { RefreshContentScope } from '../../app/ActionPlaceScopes';
import { appStore } from '../../app/AppStore';
import {
  addContentTypeToProjectHierarchy,
  populateContentBreadcrumbs,
  watchUrlForBreadcrumbUpdates
} from '../../app/content/breadcrumbs/BreadcrumbHelpers';
import { DeprecatedReactPlacePageActionsMenu } from '../../app/content/PlacePageActionsMenu';
import { FlowPlaceHeaderParts } from '../../app/content/placePages/FlowPlaceHeaderParts';
import { ContentPlaceHeaderIcons, ContentPlaceHeaderProps } from '../../app/content/placePages/PlaceHeader';
import { IPlaceScope } from '../../app/IPlaceScope';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { addUrlWatchersToScopeForExplorer } from '../../app/spaces/Explorer/ExplorerUrlBindings';
import { Messages } from '../../codegen/Localize';
import { getQueryStringServiceFromAngular } from '../../common/react/QueryString';
import { FlowSpaceOwnProps } from '../../flow/components/FlowSpace';
import { FlowSubplaceProps } from '../../flow/types/FlowSubplaceProps';
import { FlowRunEvent } from '../../flow/utils/FlowEvents';
import { AngularContext, AngularContextFactory } from '../factories/AngularContextFactory';
import { FeatureFlags } from '../FeatureFlags';
import { IScheduleFrequencyDescriptionFilter } from '../filters/ScheduleFrequencyDescriptionFilter';
import { IActionsMenuScope } from '../helpers/ActionButtonsHelper';
import { ISubplaceTabsScope, SubplaceCountsHelper, SubplaceTabsHelper } from '../helpers/SubplaceCountsHelper';
import { isFlowPlacePath } from '../helpers/UrlHelpers';
import { FlowDetailInfo, FlowDetailWithUsageInfo, FlowListItemInfo } from '../models/FlowInfo';
import { IPlace } from '../models/IPlace';
import { INamedItemInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { FlowAllowedActions } from '../ServerApi/types/FlowAllowedActions';
import { FavoriteService } from '../services/FavoriteService';
import { FlowActions } from '../services/FlowActions';
import { ServerService } from '../services/ServerService';
import { Telemetry } from '../services/TelemetryService';
import { ListActionsScope } from './ContentSubplaceCtrl';
import { FlowPublishResultService } from './FlowPublishResultCtrl';

export type FlowPlaceScope =
  IPlaceScope &
  ISubplaceTabsScope &
  IActionsMenuScope<FlowListItemInfo, {}> &
  ListActionsScope &
  RefreshContentScope &
  {
    flow: FlowDetailWithUsageInfo;
    flowPlaceHeaderProps: ContentPlaceHeaderProps;
    subplaceProps: FlowSubplaceProps;
    selectedItems: Set<FlowListItemInfo>; // TEMPORARY FOR TESTING

    stylishFlows: boolean;
    flowSpaceOwnProps: FlowSpaceOwnProps;
    hideSearch: boolean;
  };

export class FlowPlaceCtrl {

  private angularContext: AngularContext;
  private currentPath: string;
  private siteUrls: SiteUrls;

  public static $inject = [
    '$filter',
    '$location',
    '$sanitize',
    '$scope',
    '$state',
    '$translate',
    'AngularContextFactory',
    'FavoriteService',
    'FlowActions',
    'flowAllowedActions',
    'flowDetailInfo',
    'flowListItemInfo',
    'FlowPublishResultService',
    'place',
    'projectHierarchy',
    'ServerService',
    'TelemetryService',
  ];

  constructor(
    $filter: ng.IFilterService,
    private $location: ng.ILocationService,
    $sanitize: ng.sanitize.ISanitizeService,
    private $scope: FlowPlaceScope,
    private $state: ng.ui.IStateService,
    private $translate: ng.translate.ITranslateService,
    angularContextFactory: AngularContextFactory,
    private FavoriteService: FavoriteService,
    private flowActions: FlowActions,
    private flowAllowedActions: FlowAllowedActions,
    private flowDetailInfo: FlowDetailInfo,
    private flowListItemInfo: FlowListItemInfo,
    flowPublishResultService: FlowPublishResultService,
    place: IPlace,
    private projectHierarchy: INamedItemInfo[],
    private ServerService: ServerService,
    telemetry: Telemetry,
  ) {
    $scope.stylishFlows = FeatureFlags.stylishFlows;
    const angularContext = angularContextFactory.create($scope);
    const flowDetailsWithUsage = {
      usageInfo: flowListItemInfo.usageInfo,
      usageInfoMaxValues: flowListItemInfo.usageInfoMaxValues,
      ...flowDetailInfo
    };
    const siteUrls = new SiteUrls({
      siteUrlName: this.ServerService.sessionSiteUrlName(),
      queryString: window.location.search,
    });
    const subplaceProps = {
      angularContext: angularContext,
      flowAllowedActions: flowAllowedActions,
      flowDetailInfo: flowDetailInfo,
      place: place,
      scheduleFrequencyDescriptionFilter: $filter('scheduleFrequencyDescription') as IScheduleFrequencyDescriptionFilter,
      siteUrls: siteUrls,
      translate: $translate.instant.bind($translate),
    };

    // Currently only needed for Run History
    $scope.hideSearch = true;

    this.initBreadcrumbsOnScope();

    const refreshContentFn = () => $state.hardReload();
    $scope.refreshContent = refreshContentFn;
    this.currentPath = $location.path();

    if ($scope.stylishFlows) {
      $scope.flowSpaceOwnProps = {
        angularContext: angularContext,
        flowDetailInfo: flowDetailsWithUsage,
        allowedActions: this.flowAllowedActions,
        sanitizer: $sanitize,
        subplaceName: $state.current.data.subplaceName,
        subplaceProps: subplaceProps,
        telemetry: telemetry,
      };
      // add url watchers except for filters
      const queryString = getQueryStringServiceFromAngular($location);
      const getPath = () => {
        return $location.path();
      };

      addUrlWatchersToScopeForExplorer({ $scope, $location, queryString, store: appStore, getPreviousPath: getPath });

      const checkForPublishResult = (subplaceName: string) => {
        if (subplaceName === 'publishresult') {
          flowPublishResultService.handlePublishedFlow(flowDetailInfo);
          return 'overview';
        } else {
          return subplaceName;
        }
      };
      $scope.$watch(() => $state.current.data.subplaceName, () => {
        const subplaceName = checkForPublishResult($state.current.data.subplaceName);
        $scope.flowSpaceOwnProps = {
          ...$scope.flowSpaceOwnProps,
          subplaceName: subplaceName,
        };
      });
    } else {
      this.angularContext = angularContext;
      this.siteUrls = siteUrls;
      $scope.flow = flowDetailsWithUsage;
      $scope.objectName = flowDetailInfo.name;
      $scope.place = place;
      $scope.subplaceProps = subplaceProps;

      SubplaceCountsHelper.keepSubplaceCountsUpdated($scope, place);
      SubplaceTabsHelper.refreshWhenCurrentTabClicked($scope, $state);
      this.updateHeaderProps();
    }
  }

  private initBreadcrumbsOnScope() {
    const projectHierarchyWithType = addContentTypeToProjectHierarchy(this.projectHierarchy);
    const siteUrls = siteUrlsSelector(appStore.getState());
    populateContentBreadcrumbs([ ...projectHierarchyWithType, this.flowListItemInfo ], siteUrls);
    watchUrlForBreadcrumbUpdates(this.$scope, this.$location,
      () => isFlowPlacePath(this.flowListItemInfo.id, this.$location.path()));
  }

  private makePlacePageActionsMenuProps()
      : DeprecatedReactPlacePageActionsMenu.Props<FlowListItemInfo, FlowDetailInfo> {
    return {
      translate: this.$translate.instant,
      getActionTarget: () => ({
        items: Set.singleton(this.flowListItemInfo, f => f.id),
        detailedItem: this.$scope.flow,
        actionContext: { scope: this.$scope },
        allowedActions: this.flowAllowedActions,
      }),
      actions: this.flowActions.getFlowsActionButtons(),
      testId: 'flow-place-actions-menu'
    };
  }

  private updateHeaderProps() {
    const flowHeaderParts = new FlowPlaceHeaderParts(this.$scope.flow, this.onAddFavorite, this.onRemoveFavorite);
    const descriptionLineParts: ReadonlyArray<JSX.Element> =
      FeatureFlags.flowFavoritesEnabled
        ? [flowHeaderParts.favorites()]
        : [];
    this.$scope.flowPlaceHeaderProps = {
      contentTypeName: Messages.flow(),
      siteUrls: this.siteUrls,
      translate: this.$translate.instant,
      descriptionLineParts: descriptionLineParts,
      owner: this.$scope.flow.owner,
      name: this.$scope.flow.name,
      spotlightActions: [
        {
          onClick: () => {
            this.flowActions.runNow(this.flowListItemInfo, {scope: this.$scope })
              .then(() => this.angularContext.scope.$broadcast(FlowRunEvent));
          },
          iconClass: 'tb-run-now-icon',
          isAllowed: this.flowAllowedActions && this.flowAllowedActions.runFlow,
          key: 'run',
          testId: 'action-run',
          title: Messages.runNow()
        },
        {
          onClick: () => { this.flowActions.download(this.flowListItemInfo); },
          iconClass: 'tb-download-icon',
          isAllowed: this.flowAllowedActions && this.flowAllowedActions.download,
          key: 'download',
          testId: 'action-download',
          title: Messages.download()
        },
      ],
      actionsMenuProps: this.makePlacePageActionsMenuProps(),
      contentIcon: ContentPlaceHeaderIcons.flow,
    };
  }

  private readonly onAddFavorite = () => {
    this.$scope.$apply(() => {
      this.FavoriteService.addFavoriteFlow(this.$scope.flow);
      this.updateHeaderProps();
    });
  }

  private readonly onRemoveFavorite = () => {
    this.$scope.$apply(() => {
      this.FavoriteService.removeFavoriteFlow(this.$scope.flow);
      this.updateHeaderProps();
    });
  }

}

Module.instance().controller('FlowPlaceCtrl', FlowPlaceCtrl);
